import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, type SignInInput } from "aws-amplify/auth";
import { EnvelopeIcon, FingerPrintIcon } from "@heroicons/react/24/outline";
import {
    AuthError,
    AuthFooterLink,
    AuthHeading,
    BrandButton,
    BrandInput,
    GoogleButton,
    OrDivider,
} from "../components/BrandControls.tsx";
import {
    AUTH_ROUTES,
    authErrorMessage,
    continueWithGoogle,
    handleSignInNextStep,
    localSignInOptions,
    passkeyErrorMessage,
    signInNeedsPassword,
} from "../authHelpers.ts";
import { isPasskeySupported, signInWithPasskey } from "../passkey.ts";
import { rememberSignInWithoutPasskey } from "../passkeyNudge.ts";
import { requestEmailCode } from "../signInFlow.ts";
import { validateEmail } from "../validation.ts";
import { useAuthSurface } from "../authSurface.ts";
import {
    continueWithSingleSignOn,
    discoverSingleSignOn,
} from "../ssoDiscovery.ts";

const TOS_URL = `https://www.${import.meta.env.VITE_DOMAIN}/tos`;

type Step = "email" | "authenticate";

/**
 * Identifier-first, passwordless sign-in: collect the email, then offer the two
 * things the pool still accepts — a passkey, and a code sent to that address.
 *
 * Neither is announced in advance. This screen used to ask Cognito what a given
 * account had (choice-based USER_AUTH, "factor discovery") so it could show only
 * what would work. With passwords gone there are two affordances for everyone,
 * the browser decides whether a passkey ceremony is possible, and asking first
 * only told whoever typed an address which accounts exist.
 *
 * One thing is asked of our own server between the two steps: whether the
 * address's domain signs in through an organization's single sign-on
 * (ssoDiscovery.ts). That is a fact about the domain, the same for every
 * address on it, so it gives away no account. On a hit the browser leaves for
 * the organization's identity provider from the email step, and the two
 * affordances below are never offered: their account signs in through the
 * connection, whether or not the organization enforces that yet.
 *
 * Locally there is neither: cognito-local implements no passkeys and sends no
 * codes, so dev signs in with a password, and that is the only place a password
 * field is left.
 */
export default function SignInScreen({
    redirectError,
    onDismissRedirectError,
}: {
    /**
     * Why a "Continue with Google" that already left and came back did not sign
     * anyone in. It arrives as a prop because it outlives this component: the
     * redirect unmounted whatever could have held it, so `AuthFlow` reads it
     * from the auth context and hands it down. Optional, so the screen still
     * renders on its own.
     */
    redirectError?: string | null;
    onDismissRedirectError?: () => void;
} = {}) {
    const navigate = useNavigate();
    const surface = useAuthSurface();
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState<string>();
    const [passwordError, setPasswordError] = useState<string>();
    const [formError, setFormError] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const [emailCodeLoading, setEmailCodeLoading] = useState(false);
    const [continuing, setContinuing] = useState(false);

    const goToEmailStep = () => {
        setStep("email");
        setFormError(undefined);
        setPasswordError(undefined);
        setPassword("");
    };

    const onContinue = async (event: FormEvent) => {
        event.preventDefault();
        onDismissRedirectError?.();
        const emailErr = validateEmail(email);
        setEmailError(emailErr);
        if (emailErr) {
            return;
        }
        setFormError(undefined);
        if (!surface.singleSignOn) {
            setStep("authenticate");
            return;
        }

        setContinuing(true);
        let leaving = false;
        try {
            const providerName = await discoverSingleSignOn(email);
            if (providerName) {
                // No credential step: the button stays busy while the
                // browser leaves
                leaving = true;
                await continueWithSingleSignOn(providerName);
                return;
            }
            setStep("authenticate");
        } catch (err) {
            // A redirect that failed to start never left: the button is free
            // to try again
            leaving = false;
            // No answer is not "no": said plainly, and the email step stays
            setFormError(authErrorMessage(err));
        } finally {
            if (!leaving) {
                setContinuing(false);
            }
        }
    };

    /** Only reachable locally: the pool offers no password factor. */
    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        onDismissRedirectError?.();
        const passwordErr = password ? undefined : "Password is required.";
        setPasswordError(passwordErr);
        if (passwordErr) {
            return;
        }
        setLoading(true);
        setFormError(undefined);
        try {
            const input: SignInInput = {
                username: email.trim(),
                password,
                options: localSignInOptions,
            };
            const result = await signIn(input);
            rememberSignInWithoutPasskey(result, "password");
            await handleSignInNextStep(result, email.trim(), navigate);
        } catch (err) {
            setFormError(authErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const onPasskeySignIn = async () => {
        setPasskeyLoading(true);
        setFormError(undefined);
        onDismissRedirectError?.();
        try {
            const result = await signInWithPasskey(email);
            // The button is offered to everyone, so an address with no passkey
            // reaches here: Cognito answers by listing what it does have
            // instead of running a ceremony. Say so plainly — someone was just
            // asked for something they do not have — and only point at a code
            // when a code is one of the things it listed. "This email" rather
            // than "this account", because the same answer comes back for an
            // address that has no account at all.
            const nextStep = result.nextStep;
            if (
                nextStep.signInStep ===
                "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION"
            ) {
                const canUseCode =
                    nextStep.availableChallenges?.includes("EMAIL_OTP") ??
                    false;
                setFormError(
                    canUseCode
                        ? "No passkey is available for this email. Use an email code instead."
                        : "No passkey is available for this email. Email support@spectaclehq.com and we will help.",
                );
                return;
            }
            await handleSignInNextStep(result, email.trim(), navigate);
        } catch (err) {
            setFormError(passkeyErrorMessage(err));
        } finally {
            setPasskeyLoading(false);
        }
    };

    const onEmailCode = async () => {
        setEmailCodeLoading(true);
        setFormError(undefined);
        onDismissRedirectError?.();
        try {
            const result = await requestEmailCode(email);
            await handleSignInNextStep(result, email.trim(), navigate);
        } catch (err) {
            setFormError(authErrorMessage(err));
        } finally {
            setEmailCodeLoading(false);
        }
    };

    const signUpLink = surface.signUp ? (
        <AuthFooterLink
            prompt="No account yet?"
            linkLabel="Sign up"
            onClick={() => navigate(AUTH_ROUTES.signUp)}
        />
    ) : null;

    if (step === "email") {
        return (
            <>
                <AuthHeading
                    title="Welcome back!"
                    subtitle={
                        surface.marketing
                            ? "Sign in to track revenue, uncover buying journeys, and see what drives growth."
                            : "Sign in to the Spectacle admin."
                    }
                />

                <div className="flex flex-col gap-6">
                    {surface.google && (
                        <>
                            <div className="flex flex-col gap-3">
                                <GoogleButton
                                    onClick={() => {
                                        onDismissRedirectError?.();
                                        continueWithGoogle(false);
                                    }}
                                />
                                {surface.marketing && (
                                    <p className="text-center text-xs text-brand-grey">
                                        By continuing, you agree to the{" "}
                                        <a
                                            href={TOS_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="underline"
                                        >
                                            Terms of Service
                                        </a>
                                    </p>
                                )}
                            </div>

                            <OrDivider />
                        </>
                    )}

                    <form className="flex flex-col gap-8" onSubmit={onContinue}>
                        <div className="flex flex-col gap-4">
                            <AuthError
                                message={
                                    formError ?? redirectError ?? undefined
                                }
                            />
                            <BrandInput
                                label="Email"
                                type="email"
                                autoComplete="username webauthn"
                                placeholder="What’s your work email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={emailError}
                                // Held while discovery is asked about it, so
                                // the answer is for the address the next step
                                // shows
                                disabled={continuing}
                                autoFocus
                            />
                        </div>

                        <div className="flex flex-col gap-3">
                            <BrandButton type="submit" loading={continuing}>
                                Continue
                            </BrandButton>
                            {signUpLink}
                        </div>
                    </form>
                </div>
            </>
        );
    }

    // A passkey needs a browser that speaks WebAuthn and a build served from
    // the pool's relying party, which the admin console is not. A code needs
    // neither, and reaches whoever holds the address.
    const showPasskey = isPasskeySupported() && surface.passkey;

    return (
        <>
            <AuthHeading
                title="Welcome back!"
                subtitle={
                    <>
                        Signing in as{" "}
                        <span className="text-brand-purple">{email}</span>.{" "}
                        <button
                            type="button"
                            onClick={goToEmailStep}
                            className="font-medium text-brand-pink hover:underline"
                        >
                            Change
                        </button>
                    </>
                }
            />

            <div className="flex flex-col gap-6">
                {/* Above every option rather than inside the password form, so
                    a failed passkey or code request is reported even when there
                    is no password field to sit beside. */}
                <AuthError message={formError ?? redirectError ?? undefined} />

                {showPasskey && (
                    <BrandButton
                        type="button"
                        onClick={onPasskeySignIn}
                        loading={passkeyLoading}
                        disabled={emailCodeLoading}
                    >
                        <FingerPrintIcon className="h-5 w-5" />
                        Sign in with a passkey
                    </BrandButton>
                )}

                {!signInNeedsPassword && (
                    <BrandButton
                        variant={showPasskey ? "outline" : "primary"}
                        type="button"
                        onClick={onEmailCode}
                        loading={emailCodeLoading}
                        disabled={passkeyLoading}
                    >
                        <EnvelopeIcon className="h-5 w-5" />
                        Email me a code
                    </BrandButton>
                )}

                {signInNeedsPassword && (
                    <form className="flex flex-col gap-8" onSubmit={onSubmit}>
                        {/* Hidden username field so password managers associate
                            the credential on this second step. */}
                        <input
                            type="email"
                            name="username"
                            value={email}
                            readOnly
                            autoComplete="username"
                            tabIndex={-1}
                            className="sr-only"
                        />
                        <BrandInput
                            label="Password"
                            revealable
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={passwordError}
                            autoFocus
                        />

                        <div className="flex flex-col gap-3">
                            <BrandButton type="submit" loading={loading}>
                                Sign in
                            </BrandButton>
                            {signUpLink}
                        </div>
                    </form>
                )}

                {!signInNeedsPassword && signUpLink && (
                    <div className="text-center">{signUpLink}</div>
                )}
            </div>
        </>
    );
}
